import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import logger from "@/lib/logger";
import { Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Newspaper, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AI_NEWS_MOCK } from "./marketingData";

export default function NewsSection({ canEdit }) {
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/marketing/news-sources"); setSources(data.sources || []); }
    catch (e) { logger.debug("news-sources load failed", e?.message || e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => { setDraft(sources.map((s) => ({ ...s }))); setOpen(true); };
  const saveEdit = async () => {
    try {
      await api.put("/marketing/news-sources", { sources: draft });
      toast.success("News sources updated");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="mkt-news">
      <div className="card-flat p-4 mb-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Newspaper size={14} className="text-slate-700" />
          <div>
            <div className="label-overline">AI news radar</div>
            <p className="text-[11px] text-slate-500 mt-0.5">{sources.length} sources · scraped continuously, tagged for hook potential.</p>
          </div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm" onClick={startEdit} data-testid="mkt-edit-sources">Edit sources</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-xl max-h-[80vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">Edit news sources ({draft.length})</DialogTitle></DialogHeader>
              <div className="space-y-2 mt-2">
                {draft.map((s, i) => (
                  <div key={s._uid || `news-src-${i}`} className="flex items-center gap-2" data-testid={`mkt-src-row-${i}`}>
                    <Input className="rounded-sm" placeholder="Source name" value={s.name} onChange={(e) => setDraft(draft.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                    <Input className="rounded-sm flex-1" placeholder="https://…/feed" value={s.url} onChange={(e) => setDraft(draft.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} />
                    <button onClick={() => setDraft(draft.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600 p-1" data-testid={`mkt-src-remove-${i}`}><Trash2 size={11} /></button>
                  </div>
                ))}
                <Button variant="outline" className="rounded-sm w-full" onClick={() => setDraft([...draft, { _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, name: "", url: "" }])} data-testid="mkt-src-add"><Plus size={13} className="mr-1.5" />Add source</Button>
              </div>
              <DialogFooter><Button onClick={saveEdit} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="mkt-src-save">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {AI_NEWS_MOCK.map((n, i) => (
          <div key={n.url || `news-${n.title}-${i}`} className="card-flat p-3" data-testid={`mkt-news-${i}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-slate-500 font-semibold">{n.source} · {n.age}</span>
              <Tag tone={n.hook_potential >= 85 ? "red" : n.hook_potential >= 75 ? "yellow" : "slate"}>
                Hook score {n.hook_potential}
              </Tag>
            </div>
            <a href={n.url} target="_blank" rel="noreferrer noopener" className="text-sm font-display font-bold text-slate-900 hover:text-[#F54800] block">{n.title}</a>
          </div>
        ))}
      </div>
    </div>
  );
}
